import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch, FiEdit3 } from "react-icons/fi";
import { formAPI } from "../../../../../services/api";
import "./12SubjectList.css";

const VIP12SubjectList = ({ projectId = "12" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subjects, setSubjects] = useState([]); // 전체 데이터 저장
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [filteredSubjects, setFilteredSubjects] = useState([]); // 검색 필터링된 데이터
  const [paginatedSubjects, setPaginatedSubjects] = useState([]); // 페이지네이션된 데이터
  const [userValue, setUserValue] = useState(null); // 사용자 권한 값
  const [accessDenied, setAccessDenied] = useState(false); // 접근 거부 상태

  const ITEMS_PER_PAGE = 20;
  const SEARCH_FIELDS = ["data_id", "pt_number", "pt_name"]; // VIP12 검색 필드

  // 접근 거부 userValue 목록
  const DENIED_USER_VALUES = [1, 225852482, "225852482"];

  // userValue 가져오기
  useEffect(() => {
    const fetchUserValue = async () => {
      try {
        const response = await formAPI.getUserValue(projectId);
        const fetchedUserValue = response.userValue;
        setUserValue(fetchedUserValue);
        
        // userValue가 거부 목록에 있으면 접근 거부
        if (DENIED_USER_VALUES.includes(fetchedUserValue)) {
          setAccessDenied(true);
          setLoading(false);
        }
      } catch (error) {
        console.error("[12SubjectList] userValue 조회 중 오류:", error);
        // 오류 시 기본값 0으로 설정 (접근 허용)
        setUserValue(0);
      }
    };
    fetchUserValue();
  }, [projectId]);

  // URL 쿼리 파라미터 관리
  const currentParams = {
    search: searchParams.get("search") || "",
    page: parseInt(searchParams.get("page")) || 1,
  };

  // 쿼리 파라미터 업데이트 함수
  const updateQueryParams = (updates) => {
    const newParams = { ...currentParams, ...updates };
    setSearchParams(
      Object.entries(newParams)
        .filter(([_, value]) => value !== null && value !== "")
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    );
  };

  // 검색어 입력 핸들러
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // 검색 버튼 클릭 핸들러
  const handleSearch = () => {
    updateQueryParams({ search: searchInput, page: 1 });
  };

  // 검색 입력에서 Enter 키 처리
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    updateQueryParams({ page: newPage });
  };

  // VIP12 데이터 조회
  useEffect(() => {
    // accessDenied가 true이면 데이터를 가져오지 않음
    if (accessDenied) {
      return;
    }
    
    // userValue가 아직 로드되지 않았으면 대기
    if (userValue === null) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorDetails(null);

        // VIP12 백엔드 API 호출
        const vip12ApiUrl = "https://product.idea2m.com/api/vip12/get-data";
        const response = await fetch(vip12ApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            search: "",
            searchFields: SEARCH_FIELDS,
          }),
          mode: "cors",
          credentials: "omit",
        });

        if (!response.ok) {
          throw new Error(
            `API 호출 실패: ${response.status} ${response.statusText}`
          );
        }

        const responseData = await response.json();

        if (responseData.success) {
          setSubjects(responseData.rows);
          setTotalCount(responseData.totalCount);
        } else {
          throw new Error(responseData.message || "데이터 조회 실패");
        }
      } catch (err) {
        console.error("[12SubjectList] 데이터 조회 중 오류:", err);
        setErrorDetails({
          step: err.response?.data?.step,
          message: err.response?.data?.message,
          error: err.response?.data?.error,
        });
        setError(err.message || "데이터를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, userValue, accessDenied]);

  // 클라이언트 사이드 검색 필터링
  useEffect(() => {
    let filtered = subjects;

    if (currentParams.search && currentParams.search.trim()) {
      const searchTerm = currentParams.search.toLowerCase().trim();
      filtered = subjects.filter((subject) => {
        return SEARCH_FIELDS.some((field) => {
          const value = subject.form_data?.[field] || subject[field];
          return value && value.toString().toLowerCase().includes(searchTerm);
        });
      });
    }

    setFilteredSubjects(filtered);
  }, [subjects, currentParams.search]);

  // 클라이언트 사이드 페이지네이션
  useEffect(() => {
    const startIndex = (currentParams.page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginated = filteredSubjects.slice(startIndex, endIndex);
    setPaginatedSubjects(paginated);
  }, [filteredSubjects, currentParams.page]);

  // 페이지네이션 컴포넌트
  const Pagination = () => {
    const filteredTotalCount = filteredSubjects.length;
    const totalPages = Math.ceil(filteredTotalCount / ITEMS_PER_PAGE);
    const currentPage = currentParams.page;

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Total {filteredTotalCount} cases / Page {currentPage} of {totalPages}
        </div>
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          <span className="current-page-indicator">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // 편집 버튼 핸들러
  const handleEditSubject = (subject) => {
    navigate(`/ecrf/subjectlist/edit/${subject.data_id}`, {
      state: {
        projectId: "12",
        formData: subject.form_data,
      },
    });
  };

  // REBOA 그룹 배지 렌더링
  const getReboaBadge = (reboaValue) => {
    if (!reboaValue) return "-";

    const reboaConfig = {
      REBOA: { color: "#dc2626", bg: "#fee2e2" },
      "non-REBOA": { color: "#2563eb", bg: "#dbeafe" },
    };

    const config = reboaConfig[reboaValue] || {
      color: "#6b7280",
      bg: "#f3f4f6",
    };

    return (
      <span
        className="reboa-badge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "600",
        }}
      >
        {reboaValue}
      </span>
    );
  };

  // 확정 상태 배지 렌더링
  const getConfirmBadge = (confirmValue) => {
    const isConfirmed = confirmValue === "yes";

    return (
      <span
        className="confirm-badge"
        style={{
          color: isConfirmed ? "#059669" : "#6b7280",
          backgroundColor: isConfirmed ? "#d1fae5" : "#f3f4f6",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {isConfirmed ? "Confirmed" : "Pending"}
      </span>
    );
  };

  // 셀 내용 렌더링
  const renderCellContent = (subject, column) => {
    const value = subject.form_data?.[column.id] || subject[column.id];

    switch (column.type) {
      case "reboa":
        return getReboaBadge(value);
      case "confirm":
        return getConfirmBadge(value);
      default:
        return value || "-";
    }
  };

  // VIP12 테이블 컬럼 정의
  const columns = [
    {
      id: "__edit__",
      label: "Edit",
      type: "edit",
      sortable: false,
      width: "80px",
    },
    {
      id: "data_id",
      label: "Case ID",
      type: "text",
      sortable: true,
      width: "200px",
    },
    {
      id: "pt_number",
      label: "PT Number",
      type: "text",
      sortable: true,
      width: "200px",
    },
    {
      id: "pt_name",
      label: "PT Name",
      type: "text",
      sortable: true,
      width: "120px",
    },
    {
      id: "sex",
      label: "Sex",
      type: "text",
      sortable: true,
      width: "80px",
    },
    {
      id: "reboa",
      label: "REBOA",
      type: "reboa",
      sortable: true,
      width: "120px",
    },
    {
      id: "confirm",
      label: "Case Confirm",
      type: "confirm",
      sortable: true,
      width: "120px",
    },
  ];

  // 접근 거부 화면
  if (accessDenied) {
    return (
      <div className="vip-subject-list-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            padding: "40px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "12px",
              padding: "40px",
              maxWidth: "500px",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                marginBottom: "16px",
              }}
            >
              🔒
            </div>
            <h2
              style={{
                color: "#dc2626",
                fontSize: "24px",
                fontWeight: "600",
                marginBottom: "12px",
              }}
            >
              Access Restricted
            </h2>
            <p
              style={{
                color: "#6b7280",
                fontSize: "16px",
                lineHeight: "1.6",
              }}
            >
              You do not have permission to view the subject list for this project.
              <br />
              Please contact the administrator if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="vip-subject-list-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>Loading patient data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vip-subject-list-container">
        <div className="error-message">
          <h3>Error occurred</h3>
          <p>{error}</p>
          {errorDetails && (
            <div className="error-details">
              <h4>Error Details:</h4>
              <ul>
                {errorDetails.step && <li>Step: {errorDetails.step}</li>}
                {errorDetails.message && (
                  <li>Message: {errorDetails.message}</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="vip-subject-list-container"
      style={{ position: "relative", minHeight: "100vh" }}
    >
      <div className="filter-section">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder={`Search by ${SEARCH_FIELDS.join(", ")}...`}
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
          />
          <button
            onClick={handleSearch}
            className="search-button"
            title="Search"
          >
            <FiSearch className="search-button-icon" />
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="subject-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={column.sortable ? "sortable" : ""}
                  style={{ width: column.width }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedSubjects.map((subject) => (
              <tr key={subject.id}>
                {columns.map((column) => (
                  <td key={column.id}>
                    {column.id === "__edit__" ? (
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditSubject(subject)}
                        title="Edit"
                      >
                        <FiEdit3 />
                      </button>
                    ) : (
                      renderCellContent(subject, column)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination />

      {paginatedSubjects.length === 0 && !loading && (
        <div className="empty-state">
          <p>
            {filteredSubjects.length === 0
              ? "No patients found matching the criteria."
              : "No data to display on current page."}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "16px",
              padding: "8px 16px",
              backgroundColor: "#1976D2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
};

export default VIP12SubjectList;
